import classNames from 'classnames';
import React, {useCallback} from 'react';

import {
    BackendSourceOptions,
    DiscoveryItemClickEvent,
    DiscoveryItemImpressionEvent,
} from '@udemy/browse-event-tracking';
import {Tracker, TrackImpression, generateTrackingId} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Modal, ModalTrigger} from '@udemy/react-dialog-components';
import {CardMediaImage, CourseProductCard, Tag} from '@udemy/react-product-card-components';
import {CourseCardSkeleton} from '@udemy/react-reveal-components';

import {ClientSideVideoPlayer} from 'src/components/client-side-video-player';

import {UseLecturesByIdsResult, VideoLecture} from '../../../hooks/use-lectures-by-ids';
import {CardTitle} from '../../card-helpers';
import {CarouselListLayout, LayoutVariant} from '../carousel-list-layout';
import sharedStyles from '../free-resources-section.module.less';
import {withFreeResourcesLecturesData} from '../with-free-resources-lectures-data.react-component';
import styles from './free-resources-lectures-section.module.less';

const MAX_LECTURES_PER_ROW = 5;
const UI_REGION = 'bundle-unit';

interface VideoLecturePreviewProps {
    lecture: VideoLecture;
    variant: LayoutVariant;
}

interface LectureProductCardProps extends VideoLecturePreviewProps {
    index: number;
}

export interface FreeResourcesLecturesSectionProps {
    lecturesResult: UseLecturesByIdsResult;
}

const LectureProductCardSkeleton = ({variant}: LectureProductCardProps): JSX.Element => (
    <div className={classNames(styles['resource-lecture-skeleton-item'])}>
        <CourseCardSkeleton
            size={variant === LayoutVariant.Desktop ? 'medium' : 'small'}
            lineCount={1}
        />
    </div>
);

const VideoLecturePreview = ({lecture, variant}: VideoLecturePreviewProps): JSX.Element => {
    const {gettext, interpolate} = useI18n();
    const title = lecture.title ?? '';

    return (
        <ModalTrigger
            trigger={
                <button
                    type="button"
                    className={styles['lecture-modal-trigger']}
                    aria-label={interpolate(gettext('Play video: %(title)s'), {title}, true)}
                >
                    <CourseProductCard
                        className={styles['resource-lecture-item']}
                        layout={variant === LayoutVariant.Desktop ? 'vertical' : 'condensed'}
                        title={<CardTitle title={title} />}
                        mediaSquare={
                            <CardMediaImage layout="square" src={lecture.thumbnail ?? ''} />
                        }
                        mediaWide={<CardMediaImage layout="wide" src={lecture.thumbnail ?? ''} />}
                        tags={[
                            <Tag key="tag-icon">
                                <Icon glyph="google" label="Google" size="xxsmall" />
                            </Tag>,
                            <Tag key="tag-minutes">
                                {interpolate(
                                    gettext('%(minutes)s min video'),
                                    {
                                        minutes: Math.max(
                                            1,
                                            Math.ceil(Number(lecture.durationInSeconds ?? 0) / 60),
                                        ),
                                    },
                                    true,
                                )}
                            </Tag>,
                        ]}
                    />
                </button>
            }
            renderModal={(props: React.ComponentProps<typeof Modal>) => (
                <Modal
                    title={
                        <h3
                            className={classNames(
                                'ud-heading-md',
                                styles['lecture-video-modal-title'],
                            )}
                        >
                            {title}
                        </h3>
                    }
                    {...props}
                >
                    <div className={styles['lecture-video-modal-video']}>
                        <ClientSideVideoPlayer
                            asset={lecture.asset}
                            playerId={`free-resource-${lecture.asset.id}`}
                        />
                    </div>
                </Modal>
            )}
        />
    );
};

const LectureProductCard = ({lecture, index, variant}: LectureProductCardProps): JSX.Element => {
    const lectureId = Number(lecture.id);
    const trackingId = String(lecture.id);

    const trackImpression = useCallback(
        () =>
            Tracker.publishEvent(
                new DiscoveryItemImpressionEvent({
                    id: lectureId,
                    type: 'lecture',
                    trackingId: generateTrackingId(),
                    serveTrackingId: trackingId,
                    backendSource: BackendSourceOptions.DISCOVERY,
                    position: index + 1,
                    badgeFamilies: null,
                    relatedSourceId: null,
                    relatedSourceType: null,
                    uiRegion: UI_REGION,
                }),
            ),
        [lectureId, trackingId, index],
    );

    const trackClick = useCallback(
        () =>
            Tracker.publishEvent(
                new DiscoveryItemClickEvent({
                    id: lectureId,
                    type: 'lecture',
                    trackingId: generateTrackingId(),
                    serveTrackingId: trackingId,
                    backendSource: BackendSourceOptions.DISCOVERY,
                    badgeFamilies: null,
                    uiRegion: UI_REGION,
                }),
            ),
        [lectureId, trackingId],
    );

    return (
        <TrackImpression trackFunc={trackImpression}>
            <div
                onClick={trackClick}
                role="presentation"
                className={styles['resource-lecture-item-wrapper']}
            >
                <VideoLecturePreview lecture={lecture} variant={variant} />
            </div>
        </TrackImpression>
    );
};

export const FreeResourcesLecturesSection = withFreeResourcesLecturesData(
    ({lecturesResult}: FreeResourcesLecturesSectionProps) => {
        const {count, isLoading, lectures} = lecturesResult;
        const {gettext} = useI18n();

        const skeletonCount = Math.min(count, MAX_LECTURES_PER_ROW);

        const items = isLoading
            ? Array<VideoLecture>(skeletonCount).fill({} as VideoLecture)
            : lectures;

        const totalItemsCount = isLoading ? skeletonCount : lectures.length;

        const Component = isLoading ? LectureProductCardSkeleton : LectureProductCard;

        return (
            <div className={sharedStyles['resources-container']}>
                <h4 className="ud-heading-xl">{gettext('Bite-sized AI for students')}</h4>
                <div className={sharedStyles['resources-list-wrapper']}>
                    <CarouselListLayout
                        key="lectures-carousel-list-layout"
                        items={items}
                        collapsedItemsCount={MAX_LECTURES_PER_ROW}
                        totalItemsCount={totalItemsCount}
                        renderItem={(lecture, index, variant) => (
                            <Component
                                key={index}
                                lecture={lecture}
                                index={index}
                                variant={variant}
                            />
                        )}
                    />
                </div>
            </div>
        );
    },
);
